import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ChevronRight } from "lucide-react";
import { COLLECTIONS, PERLE_OPTIONS } from "@/lib/collections";

const STORE_KEY = "kgd_selection";

function loadSel() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || { collection: "", options: [] }; }
    catch { return { collection: "", options: [] }; }
}

export default function Composer() {
    const [sel, setSel] = useState(loadSel);

    useEffect(() => {
        localStorage.setItem(STORE_KEY, JSON.stringify(sel));
    }, [sel]);

    // Sync from URL if user came back with query
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        const c = p.get("collection");
        if (c && !sel.collection) setSel(s => ({ ...s, collection: c }));
    }, []); // eslint-disable-line

    const toggleOpt = (k) => setSel(s => ({
        ...s,
        options: s.options.includes(k) ? s.options.filter(x => x !== k) : [...s.options, k]
    }));

    const setColl = (slug) => setSel(s => ({ ...s, collection: s.collection === slug ? "" : slug }));

    const qs = new URLSearchParams();
    if (sel.collection) qs.set("collection", sel.collection);
    sel.options.forEach(o => qs.append("option", o));
    const goHref = "/soumission" + (qs.toString() ? `?${qs.toString()}` : "");
    const collObj = COLLECTIONS.find(x => x.slug === sel.collection);

    return (
        <section data-testid="composer-section" className="relative overflow-hidden py-20 lg:py-28" style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F8F5EE 100%)" }}>
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="text-center mb-10">
                    <div className="eyebrow">Composez votre expérience KGD</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3 curve-divider leading-[1.1]">Ajoutez votre touche Perle</h2>
                    <p className="text-slate-600 mt-8 max-w-2xl mx-auto leading-relaxed">Sélectionnez d'abord une Collection puis vos attentions Perle. Cette sélection nous aidera à préparer votre évaluation — aucun paiement ni engagement.</p>
                </div>

                {/* Collections chooser */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
                    {COLLECTIONS.map(c => {
                        const active = sel.collection === c.slug;
                        return (
                            <button
                                key={c.slug} onClick={() => setColl(c.slug)}
                                data-testid={`composer-coll-${c.slug}`}
                                className="text-left rounded-xl p-4 transition-all"
                                style={{
                                    background: active ? `linear-gradient(180deg, ${c.primary}12, #FFFFFF)` : "#FFFFFF",
                                    border: `1px solid ${active ? c.primary : c.primary + "35"}`,
                                    boxShadow: active ? `0 20px 40px -24px ${c.primary}55, inset 0 1px 0 rgba(255,255,255,0.6)` : "0 10px 30px -20px rgba(4,17,38,0.15)",
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] uppercase tracking-widest" style={{ color: c.primary === "#7DD3FC" ? "#0369A1" : c.primary }}>{c.name}</span>
                                    {active && <Check className="w-4 h-4" style={{ color: c.primary }}/>}
                                </div>
                                <div className="font-serif text-lg text-[#041126] mt-1">{c.verb}</div>
                                <div className="text-[11px] text-slate-500 mt-1 leading-snug">{c.subtitle}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Perle options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PERLE_OPTIONS.map(o => {
                        const active = sel.options.includes(o.key);
                        return (
                            <button
                                key={o.key} onClick={() => toggleOpt(o.key)}
                                data-testid={`composer-opt-${o.key}`}
                                className="text-left luxe-card p-5 transition-all"
                                style={{ borderColor: active ? "#D4AF37" : "rgba(212,175,55,0.25)", background: active ? "linear-gradient(180deg,#FFFBEE,#FFFFFF)" : undefined }}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="font-serif text-lg text-[#041126]">{o.label}</div>
                                    {active && <Check className="w-4 h-4 text-kgd-gold mt-1"/>}
                                </div>
                                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{o.desc}</p>
                                <div className="mt-3 pt-3 border-t border-kgd-gold/15 flex items-center justify-between">
                                    <span className="text-[11px] uppercase tracking-widest text-slate-500">À partir de</span>
                                    <span className="text-sm font-medium text-[#041126]">{o.from}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
                <p className="text-xs text-slate-500 mt-6 text-center max-w-3xl mx-auto leading-relaxed">Les tarifs affichés représentent des prix de départ et peuvent varier selon l'état, la superficie, la quantité et l'accessibilité.</p>

                {/* Selection summary */}
                {(sel.collection || sel.options.length > 0) && (
                    <div data-testid="composer-summary" className="mt-10 rounded-2xl p-6 sm:p-8" style={{ background: "linear-gradient(180deg,#041126,#03102A)", border: "1px solid rgba(212,175,55,0.4)" }} data-theme="dark">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="eyebrow">Votre sélection KGD</div>
                                <div className="mt-3 space-y-2">
                                    {collObj && (
                                        <div className="flex items-center gap-3 text-white">
                                            <span className="w-2 h-2 rounded-full" style={{ background: collObj.primary, boxShadow: `0 0 8px ${collObj.primary}` }}/>
                                            <span className="font-serif text-lg">Collection {collObj.name}</span>
                                            <span className="text-xs" style={{ color: "#CBD5E1" }}>— {collObj.verb}</span>
                                        </div>
                                    )}
                                    {sel.options.map(k => {
                                        const o = PERLE_OPTIONS.find(x => x.key === k);
                                        return o ? (
                                            <div key={k} className="flex items-center gap-3" style={{ color: "#E8EEF7" }}>
                                                <ChevronRight className="w-3.5 h-3.5 text-kgd-gold"/>
                                                <span className="text-sm">{o.label}</span>
                                                <span className="text-xs" style={{ color: "#CBD5E1" }}>dès {o.from}</span>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                                <p className="mt-4 text-xs max-w-md" style={{ color: "#CBD5E1" }}>
                                    Votre sélection nous aide à mieux comprendre vos besoins. Le service et le montant final seront confirmés après votre évaluation gratuite.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 sm:items-end">
                                <Link to={goHref} className="btn-kgd" data-testid="composer-cta"><Sparkles className="w-4 h-4"/> Planifier mon évaluation gratuite</Link>
                                <button onClick={() => setSel({ collection: "", options: [] })} className="text-xs uppercase tracking-widest hover:text-white" style={{ color: "rgba(255,255,255,0.5)" }} data-testid="composer-reset">Effacer ma sélection</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
